import { newId } from '@fd/schemas';

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim().replace(/\/+$/, '') || null;
const publishableKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined)?.trim() || null;

/** Build-time configuration. Only public values (no secrets) may live here. */
export const config = {
  /**
   * The Supabase project (docs/SUPABASE.md): its address and publishable key — public by design,
   * the data stays behind the owner's sign-in. Null: the development backend.
   */
  supabase: supabaseUrl && publishableKey ? { url: supabaseUrl, publishableKey } : null,
  /** The Supabase project when set; else tools/dev-backend on the machine that serves the page. */
  backendUrl:
    (supabaseUrl && publishableKey ? supabaseUrl : null) ??
    (import.meta.env.VITE_BACKEND_URL as string | undefined) ??
    `http://${location.hostname}:4600`,
};

/** Headers every call to the backend carries: Supabase's gateway wants the publishable key. */
export const backendHeaders = (): Record<string, string> => (config.supabase ? { apikey: config.supabase.publishableKey } : {});

/** Per-install device id. Not a secret — stored like any other interface preference. */
export function deviceId(): string {
  try {
    const existing = localStorage.getItem('fd.deviceId');
    if (existing) return existing;
    const created = newId();
    localStorage.setItem('fd.deviceId', created);
    return created;
  } catch {
    return newId();
  }
}

/** How this device names itself in Settings → Devices ("Edge · Windows"), and its platform. */
function deviceLabel(): { name: string; platform: 'ios' | 'android' | 'web' } {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const system = /iPhone/.test(agent) ? 'iPhone' : /iPad/.test(agent) ? 'iPad' : /Android/.test(agent) ? 'Android' : /Windows/.test(agent) ? 'Windows' : /Mac OS X/.test(agent) ? 'Mac' : 'Linux';
  const browser = /Edg\//.test(agent) ? 'Edge' : /Firefox\//.test(agent) ? 'Firefox' : /Chrome\//.test(agent) ? 'Chrome' : /Safari\//.test(agent) ? 'Safari' : 'Browser';
  const platform = system === 'iPhone' || system === 'iPad' ? 'ios' : system === 'Android' ? 'android' : 'web';
  return { name: `${browser} · ${system}`, platform };
}

/**
 * How this device introduces itself to the backend. The name goes percent-encoded: a header
 * carries ASCII only, and Supabase turned the raw "·" into "Ã¯Â¿Â½" (backend-core decodes it).
 */
export function deviceHeaders(): Record<string, string> {
  const { name, platform } = deviceLabel();
  return { 'x-device-name': encodeURIComponent(name), 'x-device-platform': platform };
}

export function readPreference(key: string): string | null {
  try {
    return localStorage.getItem(`fd.${key}`);
  } catch {
    return null;
  }
}

export function writePreference(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(`fd.${key}`);
    else localStorage.setItem(`fd.${key}`, value);
  } catch {
    // storage unavailable (private mode) — preferences simply do not persist
  }
}
